/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type { FilterDataType, RefinementType } from '@/types/filterTypes';
import {
  getBotEnabledFacetPath,
  NEAR_ME_BOT_ENABLED_FACET_PATH,
} from '@/utils/getShelfRequestStoreId';
import { parseUrl } from '@/utils/urlHelpers';

const STORE_PICKUP_DIMENSION_NAME = 'availability_options_ss';

type Options = {
  staticFilterData: FilterDataType[] | null | undefined;
  isBot: boolean;
  shelfPath: string;
  storePickupLabel: string;
};

const hasStorePickupLink = (filter: FilterDataType): boolean =>
  filter.refinements.some(
    (refinement) =>
      getBotEnabledFacetPath(refinement.properties.botsOptimizedSeoUrl) ===
      NEAR_ME_BOT_ENABLED_FACET_PATH,
  );

const getStorePickupUrl = (shelfPath: string): string => {
  const pathname = parseUrl(shelfPath).url.replace(/\/$/, '');

  return getBotEnabledFacetPath(pathname)
    ? pathname
    : `${pathname}${NEAR_ME_BOT_ENABLED_FACET_PATH}`;
};

const createStorePickupRefinement = (
  storePickupLabel: string,
  storePickupUrl: string,
  isSelected: boolean,
): RefinementType => ({
  '@class': '',
  count: 0,
  label: storePickupLabel,
  multiSelect: false,
  navigationState: '',
  properties: {
    botsOptimizedSeoUrl: storePickupUrl,
    filters: '',
    internalName: 'store_pickup',
    isSelected,
    optimizedSeoUrl: storePickupUrl,
    seoUrl: storePickupUrl,
    unCheckedOptimizedUrl: storePickupUrl,
  },
});

export const getBotStorePickupFilterData = ({
  staticFilterData,
  isBot,
  shelfPath,
  storePickupLabel,
}: Options): FilterDataType[] | null | undefined => {
  if (!isBot || staticFilterData?.some(hasStorePickupLink)) {
    return staticFilterData;
  }

  const storePickupRefinement = createStorePickupRefinement(
    storePickupLabel,
    getStorePickupUrl(shelfPath),
    getBotEnabledFacetPath(shelfPath) === NEAR_ME_BOT_ENABLED_FACET_PATH,
  );
  const availabilityFilter = staticFilterData?.find(
    (filter) => filter.dimensionName === STORE_PICKUP_DIMENSION_NAME,
  );

  if (availabilityFilter) {
    return staticFilterData?.map((filter) =>
      filter === availabilityFilter
        ? { ...filter, refinements: [storePickupRefinement, ...filter.refinements] }
        : filter,
    );
  }

  return [
    {
      '@type': 'SearchLeftNav_RefinementMenu',
      dimensionName: STORE_PICKUP_DIMENSION_NAME,
      displayName: storePickupLabel,
      multiSelect: false,
      name: storePickupLabel,
      refinements: [storePickupRefinement],
    },
    ...(staticFilterData ?? []),
  ];
};
